import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ICreateSubjectError,
  ICreateSubjectResponseBody,
  ISubject,
  ISubjectsByClassroomResponse,
  ISubjectsResponse,
  ISubjectState,
  IAssignmentResponse,
} from '../../interface/subjectModel';
import { RootState } from '../store/index';
import {
  createSubjectProfile,
  fetchSubjectsThunk,
  updateSubjectThunk,
  deleteSubjectThunk,
  getSubjectsByClassroom,
  fetchAssignmentsThunk,
  createAssignmentThunk,
  deleteAssignmentThunk,
} from '../action/subject.action';
// State interface

// Default state
const DEFAULT_STATE: ISubjectState = {
  description: undefined,
  subjectsByClassroom: null,
  isLoading: false,
  isUpdateLoading: false,
  isCreateLoading: false,
  isDeleteLoading: false,
  error: undefined,
  message: undefined,
  status: undefined,
  subjectData: [],
  total: 0,
  summary: undefined,
};

// Slice for subject state management
const subjectSlice = createSlice({
  name: 'subject',
  initialState: DEFAULT_STATE,
  reducers: {
    resetSubjectState: () => DEFAULT_STATE,
  },
  extraReducers: (builder) => {
    builder
      .addCase(createSubjectProfile.pending, (state) => {
        state.isCreateLoading = true;
        state.error = null;
        state.summary = undefined;
      })
      .addCase(
        createSubjectProfile.fulfilled,
        (state, action: PayloadAction<ICreateSubjectResponseBody>) => {
          state.isCreateLoading = false;
          state.error = null;
          state.summary = action.payload.data.summary;
        },
      )
      .addCase(
        createSubjectProfile.rejected,
        (state, action: PayloadAction<ICreateSubjectError | undefined>) => {
          state.isCreateLoading = false;
          state.summary = undefined;
          state.error = action.payload;
        },
      )
      .addCase(fetchSubjectsThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.subjectData = [];
        state.total = 0;
      })
      .addCase(fetchSubjectsThunk.fulfilled, (state, action: PayloadAction<ISubjectsResponse>) => {
        state.isLoading = false;
        state.subjectData = action.payload?.data?.subjects;
        state.total = action.payload?.data?.total;
        state.error = null;
      })
      .addCase(fetchSubjectsThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.subjectData = [];
        state.total = 0;
        state.error = action.payload;
      })
      .addCase(getSubjectsByClassroom.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.subjectsByClassroom = null;
      })
      .addCase(
        getSubjectsByClassroom.fulfilled,
        (state, action: PayloadAction<ISubjectsByClassroomResponse>) => {
          state.isLoading = false;
          state.subjectsByClassroom = action.payload.data.subjects;
          state.error = null;
        },
      )
      .addCase(getSubjectsByClassroom.rejected, (state, action) => {
        state.isLoading = false;
        state.subjectsByClassroom = null;
        state.error = action.payload;
      })
      // Update subject reducers
      .addCase(updateSubjectThunk.pending, (state) => {
        state.isUpdateLoading = true;
        state.error = null;
      })
      .addCase(updateSubjectThunk.fulfilled, (state) => {
        state.isUpdateLoading = false;
        state.error = null;
        // Data refetch handled by cache invalidation in action
      })
      .addCase(updateSubjectThunk.rejected, (state, action) => {
        state.isUpdateLoading = false;
        state.error = action.payload;
      })
      // Delete subject reducers — use a dedicated flag so the table's fetch
      // loading (isLoading) is not toggled while a delete is in progress.
      .addCase(deleteSubjectThunk.pending, (state) => {
        state.isDeleteLoading = true;
        state.error = null;
      })
      .addCase(deleteSubjectThunk.fulfilled, (state) => {
        state.isDeleteLoading = false;
        state.error = null;
        // Data refetch handled by cache invalidation in action
      })
      .addCase(deleteSubjectThunk.rejected, (state, action) => {
        state.isDeleteLoading = false;
        state.error = action.payload;
      })
      // =====================================================
      // ASSIGNMENT REDUCERS
      // =====================================================
      // Fetch assignments reducers
      .addCase(fetchAssignmentsThunk.pending, (state) => {
        state.assignmentLoading = true;
        state.assignmentError = null;
        state.assignments = [];
        state.assignmentTotal = 0;
      })
      .addCase(
        fetchAssignmentsThunk.fulfilled,
        (state, action: PayloadAction<IAssignmentResponse>) => {
          state.assignmentLoading = false;
          // Backend returns 'subjects' field (not 'assignments')
          state.assignments = action.payload?.data?.subjects;
          state.assignmentTotal = action.payload?.data?.total;
          state.assignmentError = null;
        },
      )
      .addCase(fetchAssignmentsThunk.rejected, (state, action) => {
        state.assignmentLoading = false;
        state.assignments = [];
        state.assignmentTotal = 0;
        state.assignmentError = action.payload;
      })
      // Create assignment reducers
      .addCase(createAssignmentThunk.pending, (state) => {
        state.assignmentLoading = true;
        state.assignmentError = null;
      })
      .addCase(createAssignmentThunk.fulfilled, (state) => {
        state.assignmentLoading = false;
        state.assignmentError = null;
        // Data refetch handled by cache invalidation in action
      })
      .addCase(createAssignmentThunk.rejected, (state, action) => {
        state.assignmentLoading = false;
        state.assignmentError = action.payload;
      })
      // Delete assignment reducers
      // NOTE: delete deliberately does NOT toggle `assignmentLoading` — that
      // flag drives the table spinner (fetch loading). Delete feedback is
      // handled by the confirmation modal ("Removing..."), and the list
      // refreshes via its own fetch afterwards.
      .addCase(deleteAssignmentThunk.pending, (state) => {
        state.assignmentError = null;
      })
      .addCase(deleteAssignmentThunk.fulfilled, (state) => {
        state.assignmentError = null;
        // Data refetch handled by cache invalidation in action
      })
      .addCase(deleteAssignmentThunk.rejected, (state, action) => {
        state.assignmentError = action.payload;
      });
  },
});

// Export actions and reducer
export const { resetSubjectState } = subjectSlice.actions;
export const subjectReducer = subjectSlice.reducer;

// Selectors
export const selectSubjectState = (state: RootState): ISubjectState => state.subject;
export const selectSubjectStatus = (state: RootState): string | undefined => state.subject.status;
export const selectSubjectError = (state: RootState): ICreateSubjectError | undefined =>
  state.subject.error;
export const selectSubjectMessage = (state: RootState): string | undefined => state.subject.message;
export const selectSubjectDescription = (state: RootState): string | undefined =>
  state.subject.description;
export const selectSubjectLoading = (state: RootState): boolean => state.subject.isLoading;
export const selectSubjectData = (state: RootState): ISubject[] | undefined =>
  state.subject.subjectData;

// =====================================================
// ASSIGNMENT SELECTORS
// =====================================================

export const selectAssignments = (state: RootState) => state.subject.assignments || [];
export const selectAssignmentTotal = (state: RootState): number =>
  state.subject.assignmentTotal || 0;
export const selectAssignmentLoading = (state: RootState): boolean =>
  state.subject.assignmentLoading || false;
export const selectAssignmentError = (state: RootState): ICreateSubjectError | undefined =>
  state.subject.assignmentError;

export default subjectReducer;
